import { type QueryKey, type UseBaseQueryOptions, type UseQueryResult, useQuery } from "@tanstack/react-query"
import { AxiosError, type AxiosResponse } from "axios"

import { axiosClient } from "$/http"

import { seconds } from "@/3514/utils/time.utils"

type _TBase64Response = { photo: string | null; content_type?: string | null }

type TImageUploadPhoto = { dataUrl: string; base64Value: string } | null

async function fetcher(): Promise<TImageUploadPhoto> {
    const { data }: AxiosResponse<_TBase64Response> = await axiosClient.get<_TBase64Response>("/api/user/photo-base64/")

    if (data?.photo)
        return {
            dataUrl: `data:${data?.content_type || "image/png"};base64,${data.photo}`,
            base64Value: data.photo
        }

    return null
}

type TOptions = Pick<UseBaseQueryOptions, "enabled"> & { photo: string; initialData: TImageUploadPhoto | undefined }
type TImageUploadPhotoQuery = UseQueryResult<TImageUploadPhoto, AxiosError>
function useImageUploadPhotoQuery({
    enabled,
    initialData,
    photo
}: TOptions): ReturnType<(options: TOptions) => TImageUploadPhotoQuery> {
    return useQuery<TImageUploadPhoto, AxiosError, TImageUploadPhoto, QueryKey>({
        queryKey: ["ImageUpload", photo],
        queryFn: fetcher,
        enabled,
        staleTime: 0,
        gcTime: 0,
        notifyOnChangeProps: ["data", "isError"],
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: 1,
        retryDelay: seconds(5),
        throwOnError: false,
        initialData
    })
}

export { useImageUploadPhotoQuery }
export type { TImageUploadPhoto, TImageUploadPhotoQuery }
